/**
 * What the operator sees about the visitor's device in the conversation info column: browser and its version,
 * operating system and its version, the kind of device, the screen, language and time zone. Read once per page
 * from `navigator.userAgentData` (Chromium: exact versions, the model, Windows 11 vs 10) with the User-Agent string
 * as the fallback everywhere else. Informational only — nothing is decided on it.
 */
export type SiteChatDevice = {
    browser: string | null;
    browserVersion: string | null;
    os: string | null;
    osVersion: string | null;
    type: 'desktop' | 'mobile' | 'tablet';
    model: string | null;
    screen: string | null;
    language: string | null;
    timezone: string | null;
};

type UaBrand = { brand: string; version: string };
type UaData = {
    brands?: UaBrand[];
    mobile?: boolean;
    platform?: string;
    getHighEntropyValues?(hints: string[]): Promise<{ fullVersionList?: UaBrand[]; platformVersion?: string; model?: string }>;
};

const BROWSERS: Array<[RegExp, string]> = [
    [/YaBrowser\/([\d.]+)/, 'Yandex Browser'],
    [/Edg(?:e|A|iOS)?\/([\d.]+)/, 'Edge'],
    [/OPR\/([\d.]+)/, 'Opera'],
    [/SamsungBrowser\/([\d.]+)/, 'Samsung Internet'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/FxiOS\/([\d.]+)/, 'Firefox'],
    [/CriOS\/([\d.]+)/, 'Chrome'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
];

function fromUserAgent(ua: string): Omit<SiteChatDevice, 'screen' | 'language' | 'timezone'> {
    let browser: string | null = null;
    let browserVersion: string | null = null;
    for (const [re, name] of BROWSERS) {
        const match = ua.match(re);
        if (match) {
            browser = name;
            browserVersion = match[1];
            break;
        }
    }

    let os: string | null = null;
    let osVersion: string | null = null;
    let match: RegExpMatchArray | null;
    if ((match = ua.match(/Windows NT ([\d.]+)/))) {
        os = 'Windows';
        osVersion = ({ '10.0': '10', '6.3': '8.1', '6.2': '8', '6.1': '7' } as Record<string, string>)[match[1]] ?? match[1];
    } else if ((match = ua.match(/(?:iPhone|CPU) OS ([\d_]+)/))) {
        os = 'iOS';
        osVersion = match[1].replace(/_/g, '.');
    } else if ((match = ua.match(/Android ([\d.]+)/))) {
        os = 'Android';
        osVersion = match[1];
    } else if ((match = ua.match(/Mac OS X ([\d_.]+)/))) {
        os = 'macOS';
        osVersion = match[1].replace(/_/g, '.');
    } else if (/CrOS/.test(ua)) {
        os = 'ChromeOS';
    } else if (/Linux/.test(ua)) {
        os = 'Linux';
    }

    const tablet = /iPad|Tablet|SM-T|Nexus (7|9|10)/i.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua));
    const mobile = !tablet && /Mobi|iPhone|iPod|Android/i.test(ua);
    return { browser, browserVersion, os, osVersion, type: tablet ? 'tablet' : mobile ? 'mobile' : 'desktop', model: null };
}

let cached: Promise<SiteChatDevice> | null = null;

export function readDevice(win: Window): Promise<SiteChatDevice> {
    cached ??= (async () => {
        const nav = win.navigator;
        const base = fromUserAgent(nav.userAgent || '');
        // iPadOS reports itself as a Mac; a touch screen gives it away.
        if (base.os === 'macOS' && nav.maxTouchPoints > 1)
            Object.assign(base, { os: 'iPadOS', type: 'tablet' as const });

        const data = (nav as unknown as { userAgentData?: UaData }).userAgentData;
        // Client hints refine a Chromium reading only; a Safari or Firefox string with hints attached is an
        // emulator or an extension, and the string is the better witness.
        const chromium = /Chrome|Chromium|CriOS|Edg|OPR|YaBrowser|SamsungBrowser/.test(nav.userAgent || '');
        if (chromium && data?.getHighEntropyValues) {
            try {
                const high = await data.getHighEntropyValues(['fullVersionList', 'platformVersion', 'model']);
                const brand = (high.fullVersionList ?? data.brands ?? [])
                    .find(item => !/Not.?A.?Brand|Chromium/i.test(item.brand));
                if (brand) {
                    base.browser = brand.brand.replace(/^Google /, '').replace(/^Microsoft /, '');
                    base.browserVersion = brand.version;
                }
                if (data.platform === 'Windows' && high.platformVersion)
                    base.osVersion = Number(high.platformVersion.split('.')[0]) >= 13 ? '11' : '10';
                else if (data.platform && high.platformVersion && data.platform !== 'Windows')
                    base.osVersion = high.platformVersion.replace(/_/g, '.').replace(/(\.0)+$/, '');
                if (high.model)
                    base.model = high.model;
            } catch {
                // The User-Agent reading stays.
            }
        }

        let timezone: string | null = null;
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
        } catch {
            timezone = null;
        }
        const screen = win.screen ? `${win.screen.width}×${win.screen.height}` : null;
        return { ...base, screen, language: nav.language || null, timezone };
    })();
    return cached;
}
